import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Paper,
    TableSortLabel,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "./TailwindMuiStubs";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import skLocale from "date-fns/locale/sk"; // Slovak locale
import socket, { connectSocket } from "../../socket";
import BottomBar from "../common/BottomBar";

const RaceManagementFinish = () => {
    const [crews, setCrews] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("Všetky");
    const [finishTimeFilter, setFinishTimeFilter] = useState("Všetko"); // Filter for finish time status
    const [closedFilter, setClosedFilter] = useState("Všetko"); // Filter for closed status
    const [sortOrder, setSortOrder] = useState("asc"); // Sorting order: "asc" or "desc"
    const [recordedTimes, setRecordedTimes] = useState([]); // Array to store recorded finish times
    const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control the popup visibility
    const { selectedYear } = useOutletContext();

    useEffect(() => {
        connectSocket();

        // Fetch crews and categories from the backend
        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        const fetchCategories = () => {
            socket.emit("fetchCategories");
        };

        fetchCrews();
        fetchCategories();

        socket.on("crewsData", (data) => {
            setCrews(data);
        });

        socket.on("categoriesData", (data) => {
            setCategories(data);
        });

        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );
        });

        return () => {
            socket.off("crewsData");
            socket.off("categoriesData");
            socket.off("crewUpdated");
        };
    }, [selectedYear]);

    const handleFinishTimeChange = (crewId, newTime) => {
        const isInvalidOrNull = !newTime || isNaN(newTime.getTime());
        
        const updatedCrews = crews.map((crew) =>
            crew.id === crewId
                ? {
                    ...crew,
                    race_time: {
                        ...crew.race_time,
                        finish_time: isInvalidOrNull ? null : newTime,
                    },
                }
                : crew
        );
        setCrews(updatedCrews);

        // Emit the updated finish time to the backend
        socket.emit("updateCrewField", {
            crewId,
            field: "finish_time",
            value: isInvalidOrNull ? null : newTime.toISOString(),
        });
    };

    const handleSetFinishTime = (crewId) => {
        const currentTime = new Date();

        const updatedCrews = crews.map((crew) =>
            crew.id === crewId
                ? {
                    ...crew,
                    race_time: {
                        ...crew.race_time,
                        finish_time: currentTime,
                    },
                }
                : crew
        );
        setCrews(updatedCrews);

        // Emit the current server time as the finish time to the backend
        socket.emit("updateCrewField", {
            crewId,
            field: "finish_time",
            value: currentTime.toISOString(),
        });
    };

    const handleClosedToggle = (crewId, currentClosed) => {
        const newClosed = !currentClosed;

        // Emit the updated closed status to the backend
        socket.emit("updateCrewField", { crewId, field: "closed", value: newClosed });
    };

    const handleSortToggle = () => {
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    };

    const handleExport = () => {
        console.log("Exporting data...");
        // Add export logic here
    };

    const handleClearFilters = () => {
        setSelectedCategory("Všetky");
        setFinishTimeFilter("Všetko");
        setClosedFilter("Všetko");
    };

    const handleRecordTime = () => {
        const currentTime = new Date();
        setRecordedTimes((prevTimes) => [...prevTimes, { time: currentTime, crewNumber: "" }]);
    };

    const handleRecordedTimeChange = (index, field, value) => {
        const updatedTimes = [...recordedTimes];
        updatedTimes[index][field] = value;
        setRecordedTimes(updatedTimes);
    };

    const handleSaveRecordedTime = (index) => {
        const record = recordedTimes[index];
        const crew = crews.find((c) => c.start_number === parseInt(record.crewNumber, 10));

        if (crew) {
            handleFinishTimeChange(crew.id, record.time);
            const updatedTimes = [...recordedTimes];
            updatedTimes.splice(index, 1); // Remove the saved record
            setRecordedTimes(updatedTimes);
        } else {
            alert("Štartovacie číslo neexistuje.");
        }
    };

    const handleRecordedTimeKeyDown = (index, event) => {
        if (event.key === "Enter") {
            handleSaveRecordedTime(index);
        }
    };

    const handleDeleteRecordedTime = (index) => {
        setRecordedTimes((prevTimes) => prevTimes.filter((_, i) => i !== index));
    };

    const handleOpenPopup = () => {
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
    };

    const filteredCrews = crews
        .filter((crew) => {
            // Only include crews with a start_time
            if (!crew.race_time?.start_time) {
                return false;
            }

            // Filter by selected category
            if (selectedCategory !== "Všetky" && crew.category?.name !== selectedCategory) {
                return false;
            }

            // Filter by finish time status
            if (finishTimeFilter === "S cieľovým časom" && !crew.race_time?.finish_time) {
                return false;
            }
            if (finishTimeFilter === "Bez cieľového času" && crew.race_time?.finish_time) {
                return false;
            }

            // Filter by closed status
            if (closedFilter === "Uzavreté" && !crew.race_time?.closed) {
                return false;
            }
            if (closedFilter === "Neuzavreté" && crew.race_time?.closed) {
                return false;
            }

            return true;
        })
        .sort((a, b) => {
            if (sortOrder === "asc") {
                return (a.start_number || 0) - (b.start_number || 0);
            } else {
                return (b.start_number || 0) - (a.start_number || 0);
            }
        });

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={skLocale}>
            <div className="mb-6">
                <Typography variant="h4">Cieľ Maratónu</Typography>
                <Typography variant="body2">
                    Zaznamenávanie cieľových časov a uzatváranie výsledkov posádok.
                </Typography>
            </div>

            <div style={{ 
                marginBottom: "20px", 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: "20px", 
                backgroundColor: "white",
                paddingBottom: "1rem",
                borderBottom: "1px solid #f3f4f6"
            }}>
                <FormControl sx={{ width: "100%" }}>
                    <InputLabel>Kategória</InputLabel>
                    <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <MenuItem value="Všetky">Všetky kategórie</MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.name}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ width: "100%" }}>
                    <InputLabel>Filter časov</InputLabel>
                    <Select
                        value={finishTimeFilter}
                        onChange={(e) => setFinishTimeFilter(e.target.value)}
                    >
                        <MenuItem value="Všetko">Všetko</MenuItem>
                        <MenuItem value="S cieľovým časom">S cieľovým časom</MenuItem>
                        <MenuItem value="Bez cieľového času">Bez cieľového času</MenuItem>
                    </Select>
                </FormControl>
                <FormControl sx={{ width: "100%" }}>
                    <InputLabel>Stav spracovania</InputLabel>
                    <Select
                        value={closedFilter}
                        onChange={(e) => setClosedFilter(e.target.value)}
                    >
                        <MenuItem value="Všetko">Všetko</MenuItem>
                        <MenuItem value="Uzavreté">Uzavreté</MenuItem>
                        <MenuItem value="Neuzavreté">Neuzavreté</MenuItem>
                    </Select>
                </FormControl>
            </div>
            <TableContainer
                component={Paper}
                sx={{
                    overflow: "visible", 
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={true}
                                    direction={sortOrder}
                                    onClick={handleSortToggle}
                                >
                                    Štartovacie číslo
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Posádka</TableCell>
                            <TableCell>Kategória</TableCell>
                            <TableCell>Štartovací čas</TableCell>
                            <TableCell>Cieľový čas</TableCell>
                            <TableCell>Čas celkom</TableCell>
                            <TableCell>Uzavretý</TableCell>
                            <TableCell>Akcia</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCrews.map((crew) => (
                            <TableRow key={crew.id}>
                                <TableCell>{crew.start_number || "N/A"}</TableCell>
                                <TableCell>
                                    {crew.participants
                                        ? crew.participants
                                            .map((participant) => participant.name)
                                            .join(", ")
                                        : "N/A"}
                                </TableCell>
                                <TableCell>{crew.category?.name || "N/A"}</TableCell>
                                <TableCell>
                                    {crew.race_time?.start_time
                                        ? new Date(crew.race_time.start_time).toLocaleTimeString(
                                            "sk-SK"
                                        )
                                        : "N/A"}
                                </TableCell>
                                <TableCell>
                                    <TimePicker
                                        label="Cieľový čas"
                                        value={
                                            crew.race_time?.finish_time
                                                ? new Date(crew.race_time.finish_time)
                                                : null
                                        }
                                        onChange={(newTime) =>
                                            handleFinishTimeChange(crew.id, newTime)
                                        }
                                        renderInput={(params) => <TextField {...params} />}
                                        views={["hours", "minutes", "seconds"]}
                                        disabled={crew.race_time?.closed}
                                    />
                                </TableCell>
                                <TableCell>
                                    {crew.race_time?.start_time &&
                                        crew.race_time?.finish_time
                                        ? new Date(
                                            new Date(crew.race_time.finish_time) -
                                            new Date(crew.race_time.start_time)
                                        )
                                            .toISOString()
                                            .substr(11, 8)
                                        : "N/A"}
                                </TableCell>
                                <TableCell>
                                    <Checkbox
                                        checked={crew.race_time?.closed || false}
                                        onChange={() =>
                                            handleClosedToggle(
                                                crew.id,
                                                crew.race_time?.closed || false
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSetFinishTime(crew.id)}
                                        disabled={!!crew.race_time?.finish_time}
                                    >
                                        Cieľ!
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <BottomBar
                leftContent="Zaznamenať čas"
                onLeftButtonClick={handleRecordTime}
                rightContent={recordedTimes.length > 0 ? `Zobraziť zaznamenané časy (${recordedTimes.length})` : null}
                onRightButtonClick={recordedTimes.length > 0 ? handleOpenPopup : null}
            />
            <Dialog
                open={isPopupOpen}
                onClose={(event, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        return; // Prevent closing the dialog
                    }
                    handleClosePopup(); // Allow closing for other reasons
                }}
                fullWidth
                maxWidth="md"
                BackdropProps={{
                    style: {
                        visibility: "hidden", // Hide the backdrop
                        pointerEvents: "none", // Make the backdrop click-through
                    },
                }}
                PaperProps={{
                    sx: {
                        position: "absolute", // Position the dialog absolutely
                        bottom: 60, // Set the bottom offset to 60px
                        left: 0,
                        right: 0,
                        margin: "auto", // Center horizontally
                        width: "auto", // Set the width to auto
                    },
                }}
                sx=
                {{
                    height: "calc(100vh - 60px)", // Set the height to auto
                }}

            >
                <DialogTitle>Zaznamenané časy</DialogTitle>
                <DialogContent>
                    {recordedTimes.length > 0 ? (
                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: "auto" }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Čas</TableCell>
                                        <TableCell>Štartovacie číslo</TableCell>
                                        <TableCell>Akcia</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recordedTimes.map((record, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <TextField
                                                    value={record.time.toLocaleTimeString("sk-SK")}
                                                    disabled
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    value={record.crewNumber}
                                                    onChange={(e) =>
                                                        handleRecordedTimeChange(index, "crewNumber", e.target.value)
                                                    }
                                                    onKeyDown={(e) => handleRecordedTimeKeyDown(index, e)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleSaveRecordedTime(index)}
                                                    sx={{ marginRight: 1 }}
                                                >
                                                    Uložiť
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleDeleteRecordedTime(index)}
                                                >
                                                    X
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body1">Žiadne zaznamenané časy.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePopup} color="primary">
                        Zavrieť
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default RaceManagementFinish;
